import { RouterLink as Link } from "../../../router";
import { formatCost, formatDuration, formatRelativeDate } from '@lens/lib/decode'
import { categoryColorMix, toolBarColor } from '@lens/lib/tool-categories'
import type { ProjectSummary } from '@lens/types/claude'
import { Card, CardContent, CardHeader } from "@excellent/ui"
import { Badge } from "@excellent/ui"
import { MiniBarRow } from '../mini-bar-row'

// Every language renders on the same neutral surface; the prior per-language
// table + hashed fallback palette were all the identical class string, so the
// lookup/hashing was dead code. One constant.
const LANG_BADGE = 'bg-muted text-foreground border-border'

export function ProjectCard({ project }: { project: ProjectSummary }) {
 const topTools = Object.entries(project.tool_counts ?? {})
 .sort(([, a], [, b]) => b - a)
 .slice(0, 5)
 const maxToolCount = topTools[0]?.[1] ?? 1

 const topLangs = Object.entries(project.languages ?? {})
 .sort(([, a], [, b]) => b - a)
 .slice(0, 4)

 return (
 <Card interactive asChild className="h-full gap-0 py-0 overflow-hidden">
 <Link href={`/lens/projects/${project.slug}`} className="block group">
 <CardHeader className="px-4 pt-4 pb-3 gap-2">
 {/* Title row */}
 <div className="flex items-start justify-between gap-2">
 <h3 className="type-title text-foreground group-hover:text-foreground transition-colors truncate">
 {project.display_name}
 </h3>
 <span className="type-body text-muted-foreground whitespace-nowrap shrink-0 mt-0.5">
 {formatRelativeDate(project.last_active)}
 </span>
 </div>

 {/* Path */}
 <p className="type-body text-muted-foreground truncate -mt-1">
 {project.project_path}
 </p>

 {/* Language + feature badges */}
 <div className="flex flex-wrap gap-1.5">
 {topLangs.map(([lang]) => (
 <Badge key={lang} variant="outline" className={`type-body px-1.5 py-0 h-5 ${LANG_BADGE}`}>
 {lang}
 </Badge>
 ))}
 {project.uses_mcp && (
 <Badge variant="outline" className="type-body px-1.5 py-0 h-5">MCP</Badge>
 )}
 {project.uses_task_agent && (
 <Badge variant="outline" className="type-body px-1.5 py-0 h-5">Agent</Badge>
 )}
 {project.branches.length > 0 && (
 <>
 <span
 className="h-4 w-px shrink-0 self-center bg-border/50"
 aria-hidden
 />
 {project.branches.slice(0, 3).map(b => (
 <Badge
 key={b}
 variant="outline"
 className="h-5 max-w-28 truncate border-border/50 px-1.5 py-0 type-body text-foreground"
 title={b}
 >
 {b}
 </Badge>
 ))}
 {project.branches.length > 3 && (
 <span className="self-center type-body text-muted-foreground">
 +{project.branches.length - 3}
 </span>
 )}
 </>
 )}
 </div>
 </CardHeader>

 <CardContent className="px-4 pb-4 space-y-3">
 {/* Stats row */}
 <div className="flex items-center gap-3 type-body text-muted-foreground">
 <span>{project.session_count.toLocaleString()} sessions</span>
 <span className="text-border">·</span>
 <span>{formatDuration(project.total_duration_minutes)}</span>
 {(project.total_lines_added ?? 0) > 0 && (
 <>
 <span className="text-border">·</span>
 <span className=" ">+{project.total_lines_added.toLocaleString()}</span>
 <span className=" ">-{project.total_lines_removed.toLocaleString()}</span>
 </>
 )}
 </div>

 {/* Tool bar chart */}
 {topTools.length > 0 && (
 <div className="space-y-1">
 {topTools.map(([tool, count]) => (
 <MiniBarRow
 key={tool}
 label={tool}
 value={count.toLocaleString()}
 width={Math.round((count / maxToolCount) * 100)}
 color={categoryColorMix(toolBarColor(tool), 58)}
 labelWidth="w-16"
 valueWidth="w-7"
 />
 ))}
 </div>
 )}

 {/* Cost footer */}
 <div className="flex items-center justify-between pt-2 border-t border-border/30">
 <span className="type-body text-muted-foreground">Est. cost</span>
 <span className="type-body text-foreground tabular-nums">{formatCost(project.estimated_cost)}</span>
 </div>
 </CardContent>
 </Link>
 </Card>
 )
}
